import type { AccessKeySummary } from '../types'

export type AccessKeySortKey = 'label'
export type SortDir = 'asc' | 'desc'

export interface AccessKeySortState {
  key: AccessKeySortKey
  dir: SortDir
}

/**
 * Numeric-aware case-insensitive collation. Sorts `g9 < g10 < s9 < s11`
 * the way a reader expects, not lexicographically (where `'g10' < 'g9'`
 * because `'1' < '9'`).
 */
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

/**
 * Three-way comparator that always pushes null to the end regardless of
 * sort direction. `cmp` is invoked only when both sides are non-null.
 */
function nullsLast<T>(
  a: T | null,
  b: T | null,
  cmp: (a: T, b: T) => number,
): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return cmp(a, b)
}

/**
 * Stable tie-breaker: rows with equal label fall back to `created_at`
 * descending so the residual order matches what the backend returns by
 * default (newest first).
 */
function createdAtDesc(a: AccessKeySummary, b: AccessKeySummary): number {
  return b.created_at.localeCompare(a.created_at)
}

const COMPARATORS: Record<
  AccessKeySortKey,
  (a: AccessKeySummary, b: AccessKeySummary, sign: number) => number
> = {
  label: (a, b, sign) => {
    const labelCmp = nullsLast(
      a.label,
      b.label,
      (x, y) => collator.compare(x, y) * sign,
    )
    if (labelCmp !== 0) return labelCmp
    return createdAtDesc(a, b)
  },
}

/**
 * Sort access-key summaries by the chosen key. Returns a new array;
 * input is not mutated.
 */
export function sortAccessKeys(
  keys: AccessKeySummary[],
  sort: AccessKeySortState,
): AccessKeySummary[] {
  const sign = sort.dir === 'asc' ? 1 : -1
  const cmp = COMPARATORS[sort.key]
  return keys.slice().sort((a, b) => cmp(a, b, sign))
}
